import {
  PresentationContent,
  PresentationModification,
  ToolCall,
  ToolResult,
  ChatMessage,
  ReferenceSlideImage,
} from "../../../types";
import { ModelConfig } from "../../../modelConfig";
import { buildApiUrl } from "../../../config";
import {
  AiRequestOptions,
  StreamCallbacks,
  ToolExecutionPolicy,
  ReferenceDeckExamples,
} from "../types";
import {
  readModelError,
  prepareRequestData,
  buildToolFollowUpTextInput,
  fetchWithRetry,
  tryRepairTruncatedJson,
  stripMarkdownFences,
} from "../../../utils";
import { withCredentials } from "../../apiClient";
import { withPptxLangfuseMetadata, type PptxLangfusePhase } from "../../../langfuseTrace";
import {
  executeTools,
  mergeReferenceImages,
  formatToolCallArgs,
  isToolAllowed,
  buildBlockedToolResult,
} from "../tools";
import { buildOpenAILegacyToolFollowUpPrompt, getSystemPrompt } from "../promptCatalog";
import { finalizeModelModifications } from "../modificationFinalizer";
import { sanitizeToolResultsForModelOutput } from "../logoCompanyRefs";
import { buildAgentTools, AgentToolDefinition } from "../agentToolSchemas";
import { buildOpenAIModificationResponseFormat } from "../openaiResponseFormats";
import {
  getOpenAINativeRolloutTelemetry,
  isOpenAINativeEnabled,
  recordOpenAINativeMetric,
} from "../openaiNativeRollout";
import {
  FUNCTION_CALL_NO_OUTPUT_ERROR,
  buildFallbackResultsForUnansweredCalls,
  normalizeToolResultForOutput,
  type NativeFunctionCall,
  type NativeToolOutcome,
} from "../nativeAgent/toolCalls";
import { sendNativeAgentRequest } from "../nativeAgent/transport";
import {
  MISSING_TOOL_CALL_ID_ERROR,
  runNativeAgent,
  type NativeAgentSession,
} from "../nativeAgent/runNativeAgent";
import {
  extractMessageTextContent,
  extractStructuredPayload,
  parseJsonText,
} from "../openaiResponseParsing";
import { buildOpenAIImageUrl } from "../../../utils/imagePayload";
import { mapOpenAIStreamEvent } from "../agentStream";
import {
  appendSavedContextRule,
  fetchSavedContext,
  prependSavedContext,
} from "../../../../shared/api/finster/savedContext";

// Attach per-run Langfuse trace metadata to a provider request, but only when a
// run context is present so out-of-scope PowerPoint calls stay untraced. Mirrors
// the Excel provider's per-request metadata so PPT traces carry the same
// provider/model/tool attribution for cost and debugging.
function withRequestLangfuse(
  init: RequestInit,
  options: AiRequestOptions,
  phase: PptxLangfusePhase,
  extra: Record<string, unknown> = {}
): RequestInit {
  if (!options.langfuseContext) return init;
  return withPptxLangfuseMetadata(init, options.langfuseContext, {
    phase,
    provider: "openai",
    ...extra,
  });
}

// Derive per-request Langfuse metadata (model/reasoning/stream/toolCount) from an
// outgoing Responses API body so streamed/native calls match the Excel provider's
// trace attribution.
function buildOpenAIRequestLangfuseExtra(
  requestBody: Record<string, unknown>,
  label?: string
): Record<string, unknown> {
  const reasoning = requestBody.reasoning as { effort?: unknown; mode?: unknown } | undefined;
  return {
    ...(typeof requestBody.model === "string" ? { model: requestBody.model } : {}),
    ...(label ? { label } : {}),
    ...(reasoning && typeof reasoning.effort === "string"
      ? { reasoningEffort: reasoning.effort }
      : {}),
    ...(reasoning && typeof reasoning.mode === "string" ? { reasoningMode: reasoning.mode } : {}),
    stream: requestBody.stream === true,
    ...(Array.isArray(requestBody.tools) ? { toolCount: requestBody.tools.length } : {}),
  };
}

type OpenAIReasoningOptions = Pick<AiRequestOptions, "reasoningEffort" | "reasoningMode">;

function buildOpenAIReasoning(options: OpenAIReasoningOptions): Record<string, string> {
  return {
    effort: options.reasoningEffort ?? "low",
    ...(options.reasoningMode ? { mode: options.reasoningMode } : {}),
    // Required for the Responses API to return summary_text items used by the
    // Thought process UI.
    summary: "auto",
  };
}

function resolveSeedPhase(options: AiRequestOptions): PptxLangfusePhase {
  return options.langfusePhase ?? "chat";
}

async function queryOpenAILegacy(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  model: ModelConfig,
  slideImages?: string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const { textMessage } = prepareRequestData(presentationContent, userQuery, chatHistory);
  const systemPrompt = appendSavedContextRule(getSystemPrompt(toolPolicy), options.savedContext);

  let input: unknown;
  if (slideImages && slideImages.length > 0) {
    const totalKB = slideImages.reduce((sum, img) => sum + img.length / 1024, 0);
    console.log(
      `[OPENAI] Including ${slideImages.length} slide image(s) (${totalKB.toFixed(0)}KB total) for visual context`
    );

    const imageUrls = slideImages
      .map((img) => buildOpenAIImageUrl(img))
      .filter((imageUrl): imageUrl is string => Boolean(imageUrl));
    const contentParts: unknown[] = imageUrls.map((imageUrl) => ({
      type: "input_image",
      image_url: imageUrl,
      detail: imageUrls.length > 2 ? "low" : "auto",
    }));
    contentParts.push({ type: "input_text", text: textMessage });
    input = [{ role: "user", content: contentParts }];
  } else {
    input = textMessage;
  }
  input = applyPreludeUserMessages(input, options.preludeUserMessages);

  const reasoning = buildOpenAIReasoning(options);
  const requestPayload: Record<string, unknown> = {
    model: model.model,
    instructions: systemPrompt,
    input,
    reasoning,
    text: { format: { type: "json_object" } },
    store: false,
    stream: false,
    service_tier: "priority",
  };
  const requestBody = JSON.stringify(requestPayload);

  const requestSizeKB = (requestBody.length / 1024).toFixed(1);
  console.log(
    `[OPENAI] Sending request: model=${model.model}, size=${requestSizeKB}KB, images=${slideImages?.length || 0}`
  );
  const t0 = Date.now();

  try {
    const response = await fetchWithRetry(
      buildApiUrl("/api/ai/openai/responses"),
      withRequestLangfuse(
        withCredentials({
          method: "POST",
          body: requestBody,
          signal: options.signal,
        }),
        options,
        resolveSeedPhase(options),
        {
          model: model.model,
          label: "OPENAI",
          reasoningEffort: reasoning.effort,
          ...(reasoning.mode ? { reasoningMode: reasoning.mode } : {}),
          stream: false,
        }
      ),
      3,
      "OPENAI"
    );

    if (!response.ok) {
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      const modelError = await readModelError(response, "OpenAI request failed");
      callbacks.onError(`API error (${elapsed}s): ${modelError}`);
      return;
    }

    const data = await response.json();
    const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
    console.log(
      `[OPENAI] Response received in ${elapsed}s, status=${data.status}:`,
      JSON.stringify(data).slice(0, 500)
    );

    if (data.status === "incomplete") {
      const reason = data.incomplete_details?.reason || "unknown";
      console.warn(`[OPENAI] Response was TRUNCATED (reason: ${reason})`);
      if (reason === "max_output_tokens") {
        console.warn(
          `[OPENAI] max_output_tokens limit hit — response was cut off. Will attempt to parse partial JSON.`
        );
      }
    }

    let fullContent = "";
    let reasoningContent = "";

    if (data.output && Array.isArray(data.output)) {
      for (const item of data.output) {
        if (item.type === "reasoning" && item.summary) {
          for (const s of item.summary) {
            if (s.type === "summary_text" && s.text) {
              reasoningContent += s.text;
            }
          }
        }
        if (item.type === "message" && item.content) {
          for (const c of item.content) {
            if (c.type === "output_text" && c.text) {
              fullContent = c.text;
            }
          }
        }
      }
    }

    console.log(
      `[OPENAI] Reasoning: ${reasoningContent.length} chars, Content: ${fullContent.length} chars`
    );

    if (reasoningContent) {
      callbacks.onThinking(reasoningContent);
    }

    let parsedResponse: Record<string, unknown> | null = null;
    try {
      parsedResponse = JSON.parse(fullContent);
    } catch {
      if (fullContent.includes('"slides"') && data.status === "incomplete") {
        console.log("[OPENAI] Attempting to repair truncated JSON...");
        const repaired = tryRepairTruncatedJson(fullContent);
        if (repaired) {
          try {
            parsedResponse = JSON.parse(repaired);
            fullContent = repaired;
            console.log(`[OPENAI] Successfully repaired truncated JSON (${repaired.length} chars)`);
          } catch {
            console.warn("[OPENAI] Repair attempt failed");
          }
        }
      }
    }

    if (parsedResponse?.tools && Array.isArray(parsedResponse.tools)) {
      const toolCalls: ToolCall[] = parsedResponse.tools;
      const blockedToolResults = toolCalls
        .filter((call) => !isToolAllowed(call.tool, toolPolicy))
        .map((call) =>
          buildBlockedToolResult(
            call.tool,
            `Tool ${call.tool} is disabled for this request context.`
          )
        );
      const allowedToolCalls = toolCalls.filter((call) => isToolAllowed(call.tool, toolPolicy));

      for (const call of allowedToolCalls) {
        callbacks.onToolCall(call.tool, formatToolCallArgs(call));
      }
      for (const blocked of blockedToolResults) {
        console.log(`[TOOL] Skipping disabled tool call: ${blocked.tool}`);
      }

      try {
        console.log(`[TOOL] Executing ${allowedToolCalls.length} allowed tool call(s)...`);
        const executedTools =
          allowedToolCalls.length > 0
            ? await executeTools(allowedToolCalls, {
                presentationContent,
                finsterDataSources: toolPolicy?.finsterDataSources,
                toolPolicy,
                agentToolContext: options.agentToolContext,
                langfuseContext: options.langfuseContext,
              })
            : { results: [], referenceImages: [] as ReferenceSlideImage[] };
        const toolResults = [...blockedToolResults, ...executedTools.results];
        if (toolResults.length > 0) {
          console.log("[TOOL] Results:", JSON.stringify(toolResults).slice(0, 500));
        }
        if (executedTools.referenceImages.length > 0) {
          console.log(
            `[TOOL] Captured ${executedTools.referenceImages.length} slide reference image(s) from get_slide_images`
          );
        }

        if (callbacks.onToolResult) {
          callbacks.onToolResult(toolResults);
        }

        const followUpImages = mergeReferenceImages(
          slideImages,
          flattenReferenceImages(executedTools.referenceImages)
        );
        if (followUpImages && followUpImages.length > 0) {
          console.log(
            `[TOOL] Using ${followUpImages.length} total reference image(s) for follow-up`
          );
        }

        console.log("[TOOL] Making follow-up call with tool results...");
        const followUpContent = await callWithToolResultsLegacy(
          presentationContent,
          userQuery,
          fullContent,
          toolResults,
          model,
          callbacks.onThinking,
          followUpImages,
          toolPolicy,
          options
        );

        if (followUpContent) {
          console.log(`[TOOL] Follow-up returned ${followUpContent.length} chars`);
          fullContent = followUpContent;
        } else {
          console.error("[TOOL] Follow-up returned null - no content from AI after tool results");
        }
      } catch (toolError) {
        console.error("[TOOL] Error during tool execution:", toolError);
        callbacks.onError(
          `Tool execution failed: ${toolError instanceof Error ? toolError.message : String(toolError)}`
        );
        return;
      }
    }

    if (fullContent.trim()) {
      try {
        const jsonStr = stripMarkdownFences(fullContent);

        let modifications: PresentationModification;
        try {
          modifications = JSON.parse(jsonStr);
        } catch (initialParseErr) {
          console.warn(`[OPENAI] Initial parse failed, attempting repair... (${initialParseErr})`);
          const repaired = tryRepairTruncatedJson(jsonStr);
          if (repaired) {
            modifications = JSON.parse(repaired);
            console.log(`[OPENAI] Repaired truncated JSON successfully (${repaired.length} chars)`);
          } else {
            throw initialParseErr;
          }
        }

        callbacks.onComplete(finalizeModelModifications(modifications));
      } catch (parseError) {
        console.error("Failed to parse JSON. Content length:", fullContent.length);
        console.error("FULL CONTENT:", fullContent);
        console.error("Parse error:", parseError);

        const truncationNote =
          data.status === "incomplete"
            ? " (TRUNCATED — AI response was cut off, try a simpler request)"
            : "";
        const preview =
          fullContent.length > 300
            ? fullContent.slice(0, 150) + "..." + fullContent.slice(-150)
            : fullContent;
        callbacks.onError(
          `JSON parse failed (${fullContent.length} chars)${truncationNote}: ${parseError instanceof Error ? parseError.message : "Invalid JSON"}\n\nContent: ${preview}`
        );
      }
    } else {
      console.error(
        "[OPENAI DEBUG] Empty fullContent! Reasoning was:",
        reasoningContent.slice(0, 200)
      );
      callbacks.onError(
        `Empty response from AI. Check browser console for debug info. Reasoning captured: ${reasoningContent ? "yes" : "no"}`
      );
    }
  } catch (error) {
    const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
    const errMsg = error instanceof Error ? error.message : "Unknown error";
    console.error(`[OPENAI] Request failed after ${elapsed}s:`, error);
    callbacks.onError(
      `Request failed after ${elapsed}s: ${errMsg}${errMsg === "Load failed" ? " (WebKit timeout — the AI took too long to respond)" : ""}`
    );
  }
}

async function callWithToolResultsLegacy(
  presentationContent: PresentationContent,
  userQuery: string,
  previousResponse: string,
  toolResults: ToolResult[],
  model: ModelConfig,
  onThinking?: (text: string) => void,
  slideImages?: string[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<string | null> {
  const systemPrompt = appendSavedContextRule(getSystemPrompt(toolPolicy), options.savedContext);
  const cleanResults = sanitizeToolResultsForModelOutput(toolResults);

  const followUpText = buildOpenAILegacyToolFollowUpPrompt({
    previousResponse,
    serializedToolResults: JSON.stringify(cleanResults, null, 2),
    toolPolicy,
  });
  const textInput = buildToolFollowUpTextInput(
    presentationContent,
    userQuery,
    followUpText,
    "TOOL-FOLLOWUP"
  );

  let input: unknown;
  if (slideImages && slideImages.length > 0) {
    console.log(`[TOOL-FOLLOWUP] Including ${slideImages.length} reference image(s)`);
    const contentParts: unknown[] = slideImages
      .map((img) => buildOpenAIImageUrl(img))
      .filter((imageUrl): imageUrl is string => Boolean(imageUrl))
      .map((imageUrl) => ({
        type: "input_image",
        image_url: imageUrl,
        detail: slideImages.length > 2 ? "low" : "auto",
      }));
    contentParts.push({ type: "input_text", text: textInput });
    input = [{ role: "user", content: contentParts }];
  } else {
    input = textInput;
  }
  input = applyPreludeUserMessages(input, options.preludeUserMessages);

  const t0 = Date.now();
  try {
    const reasoning = buildOpenAIReasoning(options);
    const requestPayload: Record<string, unknown> = {
      model: model.model,
      instructions: systemPrompt,
      input,
      reasoning,
      text: { format: { type: "json_object" } },
      store: false,
      service_tier: "priority",
    };
    const requestBody = JSON.stringify(requestPayload);
    const response = await fetchWithRetry(
      buildApiUrl("/api/ai/openai/responses"),
      withRequestLangfuse(
        withCredentials({
          method: "POST",
          signal: options.signal,
          body: requestBody,
        }),
        options,
        "multi_hop_executor",
        {
          model: model.model,
          label: "TOOL-FOLLOWUP",
          reasoningEffort: reasoning.effort,
          ...(reasoning.mode ? { reasoningMode: reasoning.mode } : {}),
          stream: false,
        }
      ),
      3,
      "TOOL-FOLLOWUP"
    );

    if (!response.ok) {
      console.error(
        `[TOOL-FOLLOWUP] Failed (${response.status}) after ${((Date.now() - t0) / 1000).toFixed(1)}s`
      );
      return null;
    }

    const data = await response.json();
    console.log(`[TOOL-FOLLOWUP] Response in ${((Date.now() - t0) / 1000).toFixed(1)}s`);
    const output = data.output || [];
    let contentText = "";
    let reasoningText = "";

    for (const item of output) {
      if (item.type === "reasoning" && item.summary) {
        for (const s of item.summary) {
          if (s.type === "summary_text" && s.text) {
            reasoningText += s.text;
          }
        }
      }
      if (item.type === "message" && item.content) {
        for (const c of item.content) {
          if (c.type === "output_text" && c.text) {
            contentText = c.text;
          }
        }
      }
    }

    if (reasoningText && onThinking) {
      onThinking(reasoningText);
    }

    return contentText || null;
  } catch (error) {
    const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
    console.error(`[TOOL-FOLLOWUP] Error after ${elapsed}s:`, error);
    return null;
  }
}

interface OpenAIResponseSummaryText {
  type?: string;
  text?: string;
}

interface OpenAIResponseContentItem {
  type?: string;
  text?: string;
  json?: unknown;
}

interface OpenAIResponseFunctionCallDescriptor {
  name?: string;
  arguments?: string | Record<string, unknown> | null;
  id?: string;
  call_id?: string;
}

interface OpenAIResponseOutputItem {
  type?: string;
  id?: string;
  name?: string;
  call_id?: string;
  tool_call_id?: string;
  arguments?: string | Record<string, unknown> | null;
  function?: OpenAIResponseFunctionCallDescriptor;
  summary?: OpenAIResponseSummaryText[];
  content?: OpenAIResponseContentItem[];
}

interface OpenAIResponsePayload {
  id?: string;
  status?: string;
  incomplete_details?: { reason?: string };
  output?: OpenAIResponseOutputItem[];
  output_text?: string;
}

export interface NativeFunctionCallOutputItem {
  type: "function_call_output";
  call_id: string;
  output: string;
}

function toFunctionCallOutput(
  outcome: Pick<NativeToolOutcome, "callId" | "output">
): NativeFunctionCallOutputItem {
  return {
    type: "function_call_output",
    call_id: outcome.callId,
    output: JSON.stringify(outcome.output),
  };
}

function buildFallbackOutputsForUnansweredCalls(
  calls: NativeFunctionCall[],
  errorMessage: string
): { fallbackResults: ToolResult[]; fallbackOutputs: NativeFunctionCallOutputItem[] } {
  const fallbackResults = buildFallbackResultsForUnansweredCalls(calls, errorMessage);
  const fallbackOutputs = calls.map((call, index) =>
    toFunctionCallOutput({
      callId: call.callId,
      output: normalizeToolResultForOutput(fallbackResults[index]),
    })
  );
  return { fallbackResults, fallbackOutputs };
}

interface ExtractedNativeFunctionCallsResult {
  calls: NativeFunctionCall[];
  missingCallIdCount: number;
}

interface ReplayFunctionCallState {
  unresolvedCalls: NativeFunctionCall[];
  missingCallIdCount: number;
}

function extractReasoningContent(data: OpenAIResponsePayload): string {
  let reasoningContent = "";
  const outputItems = Array.isArray(data.output) ? data.output : [];
  for (const item of outputItems) {
    if (item.type !== "reasoning" || !Array.isArray(item.summary)) {
      continue;
    }
    for (const summaryEntry of item.summary) {
      if (summaryEntry?.type === "summary_text" && typeof summaryEntry.text === "string") {
        reasoningContent += summaryEntry.text;
      }
    }
  }
  return reasoningContent;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isNativeFunctionCallType(typeValue: unknown): boolean {
  return typeValue === "function_call" || typeValue === "tool_call";
}

function extractFunctionCallIdFromItem(item: Record<string, unknown>): string | null {
  const functionPayload = asRecord(item.function);
  return (
    readNonEmptyString(item.call_id) ||
    readNonEmptyString(item.id) ||
    readNonEmptyString(item.tool_call_id) ||
    (functionPayload ? readNonEmptyString(functionPayload.call_id) : null) ||
    (functionPayload ? readNonEmptyString(functionPayload.id) : null)
  );
}

function extractFunctionNameFromItem(item: Record<string, unknown>): string {
  const functionPayload = asRecord(item.function);
  return (
    readNonEmptyString(item.name) ||
    (functionPayload ? readNonEmptyString(functionPayload.name) : null) ||
    "unknown_tool"
  );
}

function normalizeFunctionArguments(rawArguments: unknown): string {
  if (typeof rawArguments === "string") {
    return rawArguments;
  }
  if (rawArguments === undefined || rawArguments === null) {
    return "{}";
  }
  try {
    return JSON.stringify(rawArguments);
  } catch {
    return "{}";
  }
}

function extractFunctionArgumentsFromItem(item: Record<string, unknown>): string {
  if ("arguments" in item) {
    return normalizeFunctionArguments(item.arguments);
  }
  const functionPayload = asRecord(item.function);
  if (functionPayload && "arguments" in functionPayload) {
    return normalizeFunctionArguments(functionPayload.arguments);
  }
  return "{}";
}

function extractNativeFunctionCallsFromItems(items: unknown[]): ExtractedNativeFunctionCallsResult {
  const calls: NativeFunctionCall[] = [];
  let missingCallIdCount = 0;
  for (const rawItem of items) {
    const item = asRecord(rawItem);
    if (!item || !isNativeFunctionCallType(item.type)) {
      continue;
    }
    const callId = extractFunctionCallIdFromItem(item);
    if (!callId) {
      missingCallIdCount += 1;
      continue;
    }
    calls.push({
      callId,
      name: extractFunctionNameFromItem(item),
      rawArguments: extractFunctionArgumentsFromItem(item),
    });
  }
  return {
    calls,
    missingCallIdCount,
  };
}

function extractNativeFunctionCallsWithDiagnostics(
  data: OpenAIResponsePayload
): ExtractedNativeFunctionCallsResult {
  const outputItems = Array.isArray(data.output) ? data.output : [];
  return extractNativeFunctionCallsFromItems(outputItems as unknown[]);
}

function extractFunctionCallOutputIdFromItem(item: Record<string, unknown>): string | null {
  return (
    readNonEmptyString(item.call_id) ||
    readNonEmptyString(item.tool_call_id) ||
    readNonEmptyString(item.id)
  );
}

function collectReplayFunctionCallState(replayItems: unknown[]): ReplayFunctionCallState {
  const extractedCalls = extractNativeFunctionCallsFromItems(replayItems);
  const outputCallIds = new Set<string>();
  for (const rawItem of replayItems) {
    const item = asRecord(rawItem);
    if (!item || item.type !== "function_call_output") {
      continue;
    }
    const callId = extractFunctionCallOutputIdFromItem(item);
    if (callId) {
      outputCallIds.add(callId);
    }
  }

  const unresolvedCalls: NativeFunctionCall[] = [];
  const seenUnresolvedCallIds = new Set<string>();
  for (const call of extractedCalls.calls) {
    if (outputCallIds.has(call.callId) || seenUnresolvedCallIds.has(call.callId)) {
      continue;
    }
    unresolvedCalls.push(call);
    seenUnresolvedCallIds.add(call.callId);
  }

  return {
    unresolvedCalls,
    missingCallIdCount: extractedCalls.missingCallIdCount,
  };
}

function flattenReferenceImages(images: ReferenceSlideImage[] | undefined): string[] | undefined {
  if (!images || images.length === 0) return undefined;
  return images.map((image) => image.image);
}

function updateLatestReferenceImagesBySlide(
  latestImagesBySlideId: Map<string, string>,
  images: ReferenceSlideImage[],
  maxImages: number
): string[] | undefined {
  for (const { slideId, image } of images) {
    if (!slideId || !image) continue;
    latestImagesBySlideId.delete(slideId);
    latestImagesBySlideId.set(slideId, image);
  }

  while (latestImagesBySlideId.size > maxImages) {
    const oldestSlideId = latestImagesBySlideId.keys().next().value;
    if (!oldestSlideId) break;
    latestImagesBySlideId.delete(oldestSlideId);
  }

  const latestImages = Array.from(latestImagesBySlideId.values());
  return latestImages.length > 0 ? latestImages : undefined;
}

export function buildInitialUserInput(
  baseText: string,
  slideImages?: string[],
  referenceExamples?: ReferenceDeckExamples
): unknown {
  const hasSlideImages = Boolean(slideImages && slideImages.length > 0);
  const hasReference = Boolean(referenceExamples && referenceExamples.slides.length > 0);
  if (!hasSlideImages && !hasReference) {
    return baseText;
  }

  const contentParts: unknown[] = [];

  // Leading images (legacy slideImages callers) keep the images-first placement.
  if (hasSlideImages) {
    const imageUrls = slideImages!
      .map((img) => buildOpenAIImageUrl(img))
      .filter((imageUrl): imageUrl is string => Boolean(imageUrl));
    for (const imageUrl of imageUrls) {
      contentParts.push({
        type: "input_image",
        image_url: imageUrl,
        detail: imageUrls.length > 2 ? "low" : "auto",
      });
    }
  }

  contentParts.push({ type: "input_text", text: baseText });

  // Reference examples INTERLEAVED: each slide's JSON spec is immediately
  // followed by that slide's rendered image, binding spec-to-visual per slide.
  if (hasReference) {
    const ref = referenceExamples!;
    const imageCount = ref.slides.filter((slide) => slide.image).length;
    const detail = imageCount > 2 ? "low" : "auto";
    contentParts.push({ type: "input_text", text: ref.intro });
    for (const slide of ref.slides) {
      // Curated references carry an authored caption instead of a shape dump.
      const spec = [slide.caption, slide.json].filter(Boolean).join("\n");
      contentParts.push({
        type: "input_text",
        text: spec ? `${slide.label}\n${spec}` : slide.label,
      });
      const imageUrl = slide.image ? buildOpenAIImageUrl(slide.image) : null;
      if (imageUrl) {
        contentParts.push({ type: "input_image", image_url: imageUrl, detail });
      }
    }
    contentParts.push({ type: "input_text", text: ref.outro });
  }

  return [{ role: "user", content: contentParts }];
}

function normalizeInputToConversationHistory(input: unknown): unknown[] {
  if (Array.isArray(input)) {
    return [...input];
  }
  if (typeof input === "string") {
    return [{ role: "user", content: input }];
  }
  return [{ role: "user", content: String(input ?? "") }];
}

export function prependUserMessages(input: unknown, messages: readonly string[] = []): unknown[] {
  return [
    ...messages
      .filter((message) => message.trim().length > 0)
      .map((content) => ({ role: "user", content })),
    ...normalizeInputToConversationHistory(input),
  ];
}

function applyPreludeUserMessages(
  input: unknown,
  messages: readonly string[] | undefined
): unknown {
  if (!messages?.some((message) => message.trim().length > 0)) {
    return input;
  }
  return prependUserMessages(input, messages);
}

function sanitizeOutputItemForReplay(item: OpenAIResponseOutputItem): Record<string, unknown> {
  const cloned = JSON.parse(JSON.stringify(item)) as Record<string, unknown>;
  if (isNativeFunctionCallType(cloned.type)) {
    cloned.type = "function_call";
    const callId = extractFunctionCallIdFromItem(cloned);
    if (callId) {
      cloned.call_id = callId;
    }
    cloned.name = extractFunctionNameFromItem(cloned);
    cloned.arguments = extractFunctionArgumentsFromItem(cloned);
    delete cloned.function;
    delete cloned.tool_call_id;
  }
  delete cloned.id;
  return cloned;
}

function appendResponseOutputToConversationHistory(
  conversationHistory: unknown[],
  data: OpenAIResponsePayload
): unknown[] {
  const outputItems = Array.isArray(data.output) ? data.output : [];
  if (outputItems.length === 0) {
    return conversationHistory;
  }
  const sanitizedOutputItems = outputItems.map((item) => sanitizeOutputItemForReplay(item));
  return [...conversationHistory, ...sanitizedOutputItems];
}

function buildFollowUpInput(conversationReplay: unknown[], slideImages?: string[]): unknown[] {
  const inputItems: unknown[] = [...conversationReplay];
  if (slideImages && slideImages.length > 0) {
    const contentParts: unknown[] = slideImages
      .map((img) => buildOpenAIImageUrl(img))
      .filter((imageUrl): imageUrl is string => Boolean(imageUrl))
      .map((imageUrl) => ({
        type: "input_image",
        image_url: imageUrl,
        detail: slideImages.length > 2 ? "low" : "auto",
      }));
    contentParts.push({
      type: "input_text",
      text: "Below are rendered screenshot(s) from this turn showing the latest available state of the relevant slide(s). They are included as visual context for the current deck state.",
    });
    inputItems.push({ role: "user", content: contentParts });
  }
  return inputItems;
}

/**
 * Raw OpenAI Responses streaming event (a parsed `data:` SSE frame). Permissive
 * by design — only the fields the transport + UI observer read are typed.
 */
export interface OpenAIResponsesStreamEvent {
  type?: string;
  delta?: string;
  item_id?: string;
  output_index?: number;
  content_index?: number;
  arguments?: string;
  text?: string;
  item?: {
    id?: string;
    type?: string;
    name?: string;
    call_id?: string;
    arguments?: string;
  };
  response?: OpenAIResponsePayload & { error?: { message?: string; code?: string | null } };
  error?: { message?: string; code?: string | null };
  message?: string;
  code?: string | null;
}

/** Stream error codes a replay can plausibly fix; an absent code is OpenAI's generic failure. */
const TRANSIENT_OPENAI_STREAM_ERROR_CODES = new Set(["server_error", "rate_limit_exceeded"]);

/** A 200 Responses stream that OpenAI reported as failed, or that never completed. */
class OpenAIStreamError extends Error {
  readonly transient: boolean;

  constructor(message: string, transient: boolean) {
    super(message);
    this.name = "OpenAIStreamError";
    this.transient = transient;
  }
}

function isTransientOpenAIStreamError(error: unknown): boolean {
  return error instanceof OpenAIStreamError && error.transient;
}

/** Office task-pane webviews (WebView2 / modern WebKit) support fetch streaming. */
function responsesStreamingEnabled(): boolean {
  return typeof ReadableStream !== "undefined";
}

/**
 * Reads an OpenAI Responses SSE stream. Forwards every parsed event to
 * `onStreamEvent` (for live UI) and returns the terminal `response.completed`
 * payload, which is identical to the non-streaming `response.json()` body — so
 * all downstream parsing (function calls, structured output, golden capture) is
 * unchanged. The observer can never break the stream.
 */
async function readResponsesStream(
  body: ReadableStream<Uint8Array>,
  requestLabel: string,
  onStreamEvent?: (event: OpenAIResponsesStreamEvent) => void
): Promise<OpenAIResponsePayload> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let sseBuffer = "";
  let completed: OpenAIResponsePayload | null = null;
  let streamError: string | null = null;
  let streamErrorCode: string | null = null;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split("\n");
      sseBuffer = lines.pop() || "";
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(":") || trimmed.startsWith("event:")) continue;
        if (!trimmed.startsWith("data:")) continue;
        const dataStr = trimmed.slice(5).trim();
        if (!dataStr || dataStr === "[DONE]") continue;
        let event: OpenAIResponsesStreamEvent;
        try {
          event = JSON.parse(dataStr) as OpenAIResponsesStreamEvent;
        } catch {
          continue;
        }
        const type = typeof event.type === "string" ? event.type : "";
        if (type === "response.completed" || type === "response.incomplete") {
          if (event.response) completed = event.response;
        } else if (type === "response.failed" || type === "error") {
          streamError =
            event.response?.error?.message ||
            event.error?.message ||
            event.message ||
            "stream failed";
          streamErrorCode = event.response?.error?.code || event.error?.code || event.code || null;
        }
        if (onStreamEvent) {
          try {
            onStreamEvent(event);
          } catch {
            // An observer error must never abort the model stream.
          }
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // reader may already be released
    }
  }

  if (streamError) {
    throw new OpenAIStreamError(
      `${requestLabel} stream error: ${streamError}`,
      streamErrorCode === null || TRANSIENT_OPENAI_STREAM_ERROR_CODES.has(streamErrorCode)
    );
  }
  if (!completed) {
    throw new OpenAIStreamError(`${requestLabel} stream ended without a completed response`, false);
  }
  return completed;
}

/** Sends one native Responses API step through the shared native-agent transport. */
async function sendOpenAIRequest(
  requestBody: Record<string, unknown>,
  requestLabel: string,
  retries: number,
  options: AiRequestOptions = {},
  onStreamEvent?: (event: OpenAIResponsesStreamEvent) => void,
  phaseOverride?: PptxLangfusePhase,
  onReplay?: () => void
): Promise<OpenAIResponsePayload> {
  const phase = phaseOverride ?? resolveSeedPhase(options);
  const requestBodyJson = JSON.stringify(requestBody);
  const requestBytes = new TextEncoder().encode(requestBodyJson).byteLength;
  const requestUrl = buildApiUrl("/api/ai/openai/responses");
  const requestInit = withRequestLangfuse(
    withCredentials({
      method: "POST",
      body: requestBodyJson,
      signal: options.signal,
    }),
    options,
    phase,
    buildOpenAIRequestLangfuseExtra(requestBody, requestLabel)
  );
  return sendNativeAgentRequest({
    route: "/api/ai/openai/responses",
    url: requestUrl,
    init: requestInit,
    requestBytes,
    requestLabel,
    retries,
    signal: options.signal,
    // Streaming path: parse SSE and reconstruct the canonical payload from
    // `response.completed`. Falls back to JSON when the upstream did not
    // actually stream (ReadableStream unavailable, or a non-SSE body).
    readBody: async (response) => {
      const contentType = response.headers?.get("content-type") ?? "";
      if (
        requestBody.stream === true &&
        response.body &&
        contentType.includes("text/event-stream")
      ) {
        return readResponsesStream(response.body, requestLabel, onStreamEvent);
      }
      return (await response.json()) as OpenAIResponsePayload;
    },
    isTransientBodyError: isTransientOpenAIStreamError,
    onReplay,
  });
}

function createNativeRequestBody(params: {
  model: ModelConfig;
  reasoningEffort?: AiRequestOptions["reasoningEffort"];
  reasoningMode?: AiRequestOptions["reasoningMode"];
  systemPrompt: string;
  tools: AgentToolDefinition[];
  input: unknown[];
  toolChoice: "auto" | "none";
  toolPolicy?: ToolExecutionPolicy;
}): Record<string, unknown> {
  return {
    model: params.model.model,
    instructions: params.systemPrompt,
    input: params.input,
    reasoning: buildOpenAIReasoning(params),
    tools: params.tools,
    // The main agent owns PresLink intent: it decides from the request whether a
    // refresh or inspection is warranted, so no tool is ever force-picked here.
    tool_choice: params.toolChoice,
    text: { format: buildOpenAIModificationResponseFormat(params.toolPolicy) },
    store: false,
    stream: responsesStreamingEnabled(),
    service_tier: "priority",
  };
}

function parseOpenAITerminalPayload(
  responseData: OpenAIResponsePayload
): Record<string, unknown> | null {
  const structuredPayload = extractStructuredPayload(responseData);
  if (structuredPayload) return structuredPayload;
  const fallbackText = extractMessageTextContent(responseData);
  if (!fallbackText) return null;
  try {
    return parseJsonText(fallbackText);
  } catch (parseError) {
    const repaired = tryRepairTruncatedJson(fallbackText);
    if (repaired) return parseJsonText(repaired);
    recordOpenAINativeMetric("parse_failure", {
      provider: "openai",
      source: "native_message_text",
      message: parseError instanceof Error ? parseError.message : String(parseError),
    });
    return null;
  }
}

/**
 * Responses API session for the native agent.
 *
 * Each request replays the full conversation (`store: false`, no
 * `previous_response_id`). Screenshots from the latest hop are re-sent as an
 * ephemeral trailing user message holding the newest image per slide (up to
 * 16) and are not kept in the replayed history.
 */
function createOpenAISession(params: {
  model: ModelConfig;
  systemPrompt: string;
  tools: AgentToolDefinition[];
  seedInput: unknown[];
  toolPolicy?: ToolExecutionPolicy;
  callbacks: StreamCallbacks;
  options: AiRequestOptions;
}): NativeAgentSession {
  const { model, systemPrompt, tools, toolPolicy, callbacks, options } = params;
  const includeApplyEditArgsDeltas = options.streamApplyEditSlideSummaries === true;
  const latestReferenceImagesBySlideId = new Map<string, string>();
  let conversationHistory: unknown[] = params.seedInput;
  let pendingOutputs: NativeFunctionCallOutputItem[] = [];
  let referenceImages: string[] | undefined;
  let lastResponse: OpenAIResponsePayload = {};
  let seeded = false;

  const hopObserver = (hop: number) => {
    const onAgentEvent = callbacks.onAgentEvent;
    if (!onAgentEvent) return undefined;
    return (event: OpenAIResponsesStreamEvent) => {
      for (const mapped of mapOpenAIStreamEvent(event, hop, { includeApplyEditArgsDeltas })) {
        onAgentEvent(mapped);
      }
    };
  };

  const buildReplay = (): unknown[] => {
    let replay: unknown[] = [...conversationHistory, ...pendingOutputs];
    pendingOutputs = [];
    const replayState = collectReplayFunctionCallState(replay);
    if (replayState.missingCallIdCount > 0) {
      recordOpenAINativeMetric("tool_loop_failure", {
        provider: "openai",
        reason: "missing_call_id",
        stage: "followup",
        missingCallIdCount: replayState.missingCallIdCount,
      });
      throw new Error(MISSING_TOOL_CALL_ID_ERROR);
    }
    if (replayState.unresolvedCalls.length > 0) {
      const reconciliation = buildFallbackOutputsForUnansweredCalls(
        replayState.unresolvedCalls,
        FUNCTION_CALL_NO_OUTPUT_ERROR
      );
      recordOpenAINativeMetric("tool_loop_replay_reconciled", {
        provider: "openai",
        stage: "followup",
        unresolvedCalls: replayState.unresolvedCalls.length,
      });
      replay = [...replay, ...reconciliation.fallbackOutputs];
    }
    return replay;
  };

  return {
    provider: "openai",
    requestLabel: "OPENAI-NATIVE",
    async sendTurn(request) {
      const replay = seeded ? buildReplay() : conversationHistory;
      const input = seeded ? buildFollowUpInput([...replay], referenceImages) : replay;
      seeded = true;
      lastResponse = await sendOpenAIRequest(
        createNativeRequestBody({
          model,
          reasoningEffort: options.reasoningEffort,
          reasoningMode: options.reasoningMode,
          systemPrompt,
          tools,
          input,
          toolChoice: request.toolChoice,
          toolPolicy,
        }),
        request.label,
        request.retries,
        options,
        hopObserver(request.hop),
        request.phase,
        () => callbacks.onAgentEvent?.({ kind: "hop_reset", hop: request.hop })
      );
      conversationHistory = appendResponseOutputToConversationHistory(replay, lastResponse);
      const extracted = extractNativeFunctionCallsWithDiagnostics(lastResponse);
      return {
        calls: extracted.calls,
        missingCallIdCount: extracted.missingCallIdCount,
        reasoning: extractReasoningContent(lastResponse),
      };
    },
    appendToolOutcomes(outcomes, images) {
      pendingOutputs = [...pendingOutputs, ...outcomes.map(toFunctionCallOutput)];
      if (images.length > 0) {
        referenceImages = updateLatestReferenceImagesBySlide(
          latestReferenceImagesBySlideId,
          [...images],
          16
        );
      }
    },
    pendingCalls() {
      const replayState = collectReplayFunctionCallState([
        ...conversationHistory,
        ...pendingOutputs,
      ]);
      return {
        calls: replayState.unresolvedCalls,
        missingCallIdCount: replayState.missingCallIdCount,
      };
    },
    terminalPayload() {
      return parseOpenAITerminalPayload(lastResponse);
    },
    toProviderResultItem(outcome) {
      return { functionCallOutput: toFunctionCallOutput(outcome) };
    },
  };
}

async function queryOpenAINative(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  model: ModelConfig,
  slideImages?: string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const systemPrompt = appendSavedContextRule(getSystemPrompt(toolPolicy), options.savedContext);
  const { textMessage } = prepareRequestData(presentationContent, userQuery, chatHistory);
  const initialInput = buildInitialUserInput(textMessage, slideImages, options.referenceExamples);
  const session = createOpenAISession({
    model,
    systemPrompt,
    tools: buildAgentTools(toolPolicy),
    seedInput: prependUserMessages(initialInput, options.preludeUserMessages),
    toolPolicy,
    callbacks,
    options,
  });
  await runNativeAgent({ session, presentationContent, callbacks, toolPolicy, options });
}

export async function queryOpenAI(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  model: ModelConfig,
  slideImages?: string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const telemetry = getOpenAINativeRolloutTelemetry();
  console.log(
    `[OPENAI_NATIVE] rollout enabled=${telemetry.enabled} stage=${telemetry.stage}${telemetry.bucket !== undefined ? ` bucket=${telemetry.bucket}` : ""} source=${telemetry.source}`
  );

  const savedContext = await fetchSavedContext(options.agentToolContext?.log);
  const runOptions: AiRequestOptions = savedContext
    ? {
        ...options,
        savedContext,
        preludeUserMessages: prependSavedContext(options.preludeUserMessages, savedContext),
      }
    : options;

  if (!isOpenAINativeEnabled()) {
    recordOpenAINativeMetric("legacy_fallback_invoked", { reason: "rollout_disabled" });
    return queryOpenAILegacy(
      presentationContent,
      userQuery,
      callbacks,
      model,
      slideImages,
      chatHistory,
      toolPolicy,
      runOptions
    );
  }

  return queryOpenAINative(
    presentationContent,
    userQuery,
    callbacks,
    model,
    slideImages,
    chatHistory,
    toolPolicy,
    runOptions
  );
}
